import { saveAs } from "file-saver";
import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";

import { myAxios, validURL } from "src/components/common/functions";
import { appendField } from "../common/FilterFrame/FilterField";
import { buildDefaultState } from "../common/FilterFrame/FilterModal";
import { fields } from "./VendorFilterForm";
import { extraFields } from "./VendorList";

export async function vendorsLoader({ request }: LoaderFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const url = new URL(request.url);
    let shouldRedirect = false;
    for (const [key] of url.searchParams) {
        if (![...Object.keys(buildDefaultState(fields)), ...extraFields].includes(key)) {
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }
    if (!url.searchParams.get("page")) {
        url.searchParams.set("page", "1");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("limit")) {
        url.searchParams.set("limit", "10");
        shouldRedirect = true;
    }
    if (shouldRedirect) {
        throw redirect(url.pathname + "?" + url.searchParams.toString());
    }
    const formData = new FormData();
    formData.append("page", url.searchParams.get("page")!);
    formData.append("limit", url.searchParams.get("limit")!);
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({
        fileName: "vendor/readVendors.php",
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect("..");
    return res.data;
}

export async function vendorLoader({ params, request }: LoaderFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const { id } = params;
    if (!id) {
        throw redirect("/Vendors");
    }
    const formData = new FormData();
    formData.append("id", id);
    const res = await myAxios({
        fileName: "vendor/readVendor.php",
        formData,
        signal: controller.signal,
    });
    if (!res || !res.data) throw redirect("..");
    return {
        vendor: res.data,
    };
}

const urlMap = { Add: "vendor/createVendor.php", Edit: "vendor/updateVendor.php" };

export async function vendorAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    const action = formData.get("action") as keyof typeof urlMap;
    const url = urlMap[action];
    if (!url) {
        window.alert("Invalid Keyword");
        return null;
    }
    if (formData.get("website") && !validURL(formData.get("website") as string)) {
        window.alert("Invalid Website Format");
        return null;
    }
    if (!formData.get("force")) {
        const duplicateRes = await myAxios({
            fileName: "vendor/checkDuplicateVendor.php",
            formData,
            signal: controller.signal,
        });
        if (!duplicateRes) return null;
        if (Array.isArray(duplicateRes.data) && duplicateRes.data.length) {
            const labels = duplicateRes.data.map((item: { vendorName: string }) => item.vendorName).join("\n");
            if (!window.confirm(`Potential duplicates found:\n${labels}\n\nCreate/update anyway?`)) {
                return null;
            }
            formData.append("force", "1");
        }
    }
    const res = await myAxios({ fileName: url, formData, signal: controller.signal });
    if (!res) return null;
    if (action === "Add") {
        return redirect(`/Vendors/${res.data.id}`);
    }
    return redirect("..");
}

export async function deleteVendorAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    if (!formData.get("id")) throw new Response("Missing id", { status: 400 });
    const res = await myAxios({
        fileName: "vendor/deleteVendor.php",
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect("..");
    throw redirect("../..");
}

export async function voidVendorAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    if (!formData.get("id")) throw new Response("Missing id", { status: 400 });
    await myAxios({
        fileName: "vendor/voidVendor.php",
        formData,
        signal: controller.signal,
    });
    throw redirect("..");
}

export async function validateVendorAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    if (!formData.get("id")) throw new Response("Missing id", { status: 400 });
    await myAxios({
        fileName: "vendor/validateVendor.php",
        formData,
        signal: controller.signal,
    });
    throw redirect("..");
}

export async function vendorExportAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const url = new URL(request.url);
    const formData = await request.formData();
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({
        fileName: "vendor/exportVendors.php",
        formData,
        responseType: "blob",
        signal: controller.signal,
    });
    if (!res) throw redirect(`..?${url.searchParams.toString()}`);
    const disposition = res.headers["content-disposition"];
    let filename = "vendors.xlsx";
    if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = decodeURIComponent(match[1]);
    }
    saveAs(
        new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        filename
    );
    throw redirect(`..?${url.searchParams.toString()}`);
}
